import { useState } from "react";
import type { Node, Plan } from "@melo/shared";
import { store, useTables } from "../lib/store";
import { byRank, keyOf } from "../lib/select";
import { send } from "../lib/actions";
import { toast } from "../lib/toast";
import { Avatar, Avatars } from "./primitives";
import { can } from "../lib/access";
import { ItemLink } from "./ItemLink";

/** An agent's proposal: the person unticks what they do not want and approves the rest. */
export function PlanCard({ plan, highlight }: { plan: Plan; highlight?: boolean }) {
  const t = useTables();
  const drafts = [...t.nodes.values()].filter((n) => n.planId === plan.id && n.draft).sort(byRank);
  const [off, setOff] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const target = plan.targetId ? t.nodes.get(plan.targetId) : undefined;
  const space = t.spaces.get(plan.spaceId);
  const who = t.actors.get(plan.proposedBy);
  const depth = (n: Node) => {
    let d = 0;
    for (let p = n.parentId ? t.nodes.get(n.parentId) : undefined; p && p.draft && d < 4; p = p.parentId ? t.nodes.get(p.parentId) : undefined) d++;
    return d;
  };
  const keep = drafts.filter((d) => !off.has(d.id));
  const decide = async (decision: "approve" | "discard") => {
    setBusy(true);
    try {
      await send("plan.decide", { planId: plan.id, decision, keepIds: decision === "approve" ? keep.map((k) => k.id) : undefined });
      toast(decision === "approve" ? `Added ${keep.length} item${keep.length === 1 ? "" : "s"} to ${space?.name}` : "Plan discarded");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`plan${highlight ? " highlight" : ""}`} id={`plan-${plan.id}`}>
      <div className="plan-h">
        <Avatar actor={who} size="sm" />
        <div className="grow">
          <b>{plan.summary}</b>
          <small>
            {who?.name} proposed {drafts.length} item{drafts.length === 1 ? "" : "s"} in {space?.name}
            {target ? (
              <>
                {" "}for <ItemLink itemKey={keyOf(t, target)}>{target.title}</ItemLink>
              </>
            ) : null}
          </small>
        </div>
      </div>
      <div className="plan-list">
        {drafts.map((d) => {
          const kind = t.kinds.get(d.kindId);
          const discipline = typeof d.fields.discipline === "string" ? d.fields.discipline : null;
          return (
            <label key={d.id} className="plan-i" style={{ paddingLeft: 12 + depth(d) * 18 }}>
              <input
                type="checkbox"
                checked={!off.has(d.id)}
                onChange={(e) => {
                  const next = new Set(off);
                  if (e.target.checked) next.delete(d.id);
                  else next.add(d.id);
                  setOff(next);
                }}
              />
              <span className="grow">{d.title}</span>
              {discipline && <span className="chip mono">{discipline}</span>}
              {kind && kind.key !== "task" && <span className="chip">{kind.name}</span>}
              <Avatars actors={d.assignees.map((a) => store.t.actors.get(a.actorId))} />
            </label>
          );
        })}
      </div>
      <div className="plan-f">
        {!can(plan.spaceId, "producer") && <span className="hint grow">A producer on {space?.name} approves this.</span>}
        <button type="button" className="btn ghost sm" disabled={busy || !can(plan.spaceId, "producer")} onClick={() => void decide("discard")}>
          Discard
        </button>
        <button type="button" className="btn pri sm" disabled={busy || !keep.length || !can(plan.spaceId, "producer")} onClick={() => void decide("approve")}>
          Approve {keep.length}
        </button>
      </div>
    </div>
  );
}
